<template>
  <section style="position: relative">
    <p v-if="detail.mark_remove" class="is-size-4 mark-remove-detail">
      刪除
    </p>
    <b-field :label="$t('attributes.name')" horizontal>
      <b-input v-model="detail.name" :disabled="!editing" required />
    </b-field>

    <b-field :label="$t('attributes.english_name')" horizontal>
      <b-input v-model="detail.english_name" :disabled="!editing" />
    </b-field>

    <b-field :label="$t('attributes.emergency_contact')" horizontal>
      <b-input
        v-model="detail.emergency_contact"
        :disabled="!editing"
        required
      />
    </b-field>

    <b-field :label="$t('attributes.emergency_phone')" horizontal>
      <b-input v-model="detail.emergency_phone" :disabled="!editing" required />
    </b-field>

    <b-field :label="$t('attributes.identity_number')" horizontal>
      <b-input v-model="detail.identity_number" :disabled="!editing" />
    </b-field>

    <b-field :label="$t('attributes.relationship')" horizontal>
      <b-select
        v-model="detail.relationship"
        :disabled="!editing || first"
        placeholder="與業務同仁關係"
        required
      >
        <option
          v-for="(option, index) in relationshipOptions"
          :value="option"
          :key="index"
          :disabled="index === 0 && !first"
        >
          {{ option }}
        </option>
      </b-select>
    </b-field>

    <b-field :label="$t('attributes.home_address')" horizontal>
      <b-input v-model="detail.home_address" :disabled="!editing" />
    </b-field>

    <b-field :label="$t('attributes.passport_number')" horizontal>
      <b-input v-model="detail.passport_number" :disabled="!editing" />
    </b-field>

    <b-field :label="$t('attributes.passport_expire')" horizontal>
      <b-datepicker
        v-model="detail.passport_expire"
        :disabled="!editing"
        :date-formatter="formatDate"
        placeholder="護照效期"
        icon="calendar-today"
      >
      </b-datepicker>
    </b-field>

    <b-field :label="$t('attributes.gender')" horizontal>
      <b-select v-model="detail.gender" :disabled="!editing" placeholder="性別">
        <option
          v-for="(option, index) in genderOptions"
          :value="option"
          :key="index"
        >
          {{ option }}
        </option>
      </b-select>
    </b-field>

    <b-field :label="$t('attributes.date_of_birth')" horizontal>
      <b-datepicker
        v-model="detail.date_of_birth"
        :disabled="!editing"
        :date-formatter="formatDate"
        placeholder="出生日期"
        icon="calendar-today"
        required
      >
      </b-datepicker>
    </b-field>

    <b-field :label="$t('attributes.age')" horizontal>
      <b-input :value="detail.age" disabled />
    </b-field>

    <b-field :label="$t('attributes.age_type')" horizontal>
      <b-input :value="ageTypes[detail.age_type]" disabled />
    </b-field>

    <b-field :label="$t('attributes.bed_type')" horizontal>
      <b-select
        v-model="detail.bed_type"
        :disabled="!editing"
        placeholder="旅客類別"
      >
        <option
          v-for="(option, index) in bedOptions"
          :value="option.value"
          :key="index"
        >
          {{ option.name }}
        </option>
      </b-select>
    </b-field>

    <b-field :label="$t('attributes.email')" horizontal>
      <b-input v-model="detail.email" :disabled="!editing" required />
    </b-field>

    <b-field :label="$t('attributes.mobile_phone')" horizontal>
      <b-input v-model="detail.mobile_phone" :disabled="!editing" required />
    </b-field>

    <b-field :label="$t('attributes.company_tel')" horizontal>
      <b-input v-model="detail.tel" :disabled="!editing" />
    </b-field>

    <b-field :label="$t('attributes.remark')" horizontal>
      <b-input v-model="detail.remark" :disabled="!editing" type="textarea" />
    </b-field>

    <section
      v-if="detail.common_answers.length > 0"
      style="margin-bottom: 0.75rem;"
    >
      <div class="has-text-centered is-size-5 has-text-weight-semibold">
        參團資料
      </div>

      <b-field
        v-for="(question, index) in detail.common_answers"
        :key="index"
        :label="question.title"
        horizontal
        class="field-checkbox"
      >
        <b-field v-for="(option, index) in question.options" :key="index">
          <b-radio
            v-if="question.type === 0"
            v-model="question.answers"
            :native-value="option"
            :disabled="!editing"
          >
            {{ option.name }}
          </b-radio>
          <b-checkbox
            v-else
            v-model="question.answers"
            :native-value="option"
            :disabled="!editing"
          >
            {{ option.name }}
          </b-checkbox>
          <b-input
            v-if="option.type === 'Text'"
            v-model="option.value"
            :disabled="!editing"
          />
        </b-field>
      </b-field>
    </section>

    <section
      v-if="detail.spot_answers.length > 0"
      style="margin-bottom: 0.75rem;"
    >
      <div class="has-text-centered is-size-5 has-text-weight-semibold">
        參團行程
      </div>

      <b-field
        v-for="(question, index) in detail.spot_answers"
        :key="index"
        :label="question.title"
        horizontal
      >
        <b-select
          v-model="question.selected_spot"
          :disabled="!editing"
          placeholder=""
          @input="spot_option => onSelectedSpot(question, spot_option)"
        >
          <option :value="null" disabled>請選擇行程</option>
          <option
            v-for="(spot_option, index) in question.spot_options"
            :value="spot_option"
            :key="index"
            :disabled="spot_option.sign_up_quantity >= spot_option.quantity"
          >
            ({{ spot_option.sign_up_quantity }} / {{ spot_option.quantity }})
            {{ spot_option.name }}
          </option>
        </b-select>
      </b-field>
    </section>

    <b-field v-show="canEdit" horizontal style="margin-bottom: 0.75rem;">
      <div class="buttons is-right">
        <button
          v-if="!editing"
          @click.prevent="onEditing"
          class="button is-info"
        >
          修改暫存資料
        </button>
        <button
          v-if="editing"
          @click.prevent="onCancel"
          class="button is-light"
        >
          取消
        </button>
        <button
          v-if="editing"
          @click.prevent="onSave"
          class="button is-primary"
        >
          更新暫存資料
        </button>
      </div>
    </b-field>
    <b-loading :is-full-page="true" :active="isSending" :can-cancel="false" />
  </section>
</template>

<script>
import notifyService from '@/services/notifyService'
import { USER_ORDER_DETAILS_API } from '@/utils/apis'

export default {
  name: 'OrderDetail',

  props: {
    first: {
      type: Boolean,
      default: false
    },
    travelGroup: {
      type: Object,
      default: null
    },
    originDetail: {
      type: Object,
      default: null
    },
    referenceDetail: {
      type: Object,
      default: null
    },
    commonQuestions: {
      type: Array,
      default: () => []
    },
    spotQuestions: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      editing: false,
      isSending: false,
      detail: this.extractDetail(this.originDetail),
      relationshipOptions: ['本人', '父母', '子女', '配偶', '其他'],
      genderOptions: ['男', '女'],
      ageTypes: ['成人', '孩童', '嬰兒'],
      bedOptions: [
        { value: 0, name: '成人(佔床)' },
        { value: 1, name: '孩童(佔床)' },
        { value: 2, name: '孩童(不佔床)' },
        { value: 3, name: '嬰兒' },
        { value: 4, name: '加床' }
      ]
    }
  },
  watch: {
    originDetail(detail) {
      this.detail = this.extractDetail(detail)
    },
    'detail.date_of_birth'(birthDate) {
      this.updateAge(
        birthDate,
        this.travelGroup.departure_date,
        this.travelGroup.child_age,
        this.travelGroup.baby_age
      )
    }
  },
  computed: {
    canEdit() {
      return !this.disabled && !this.detail.mark_remove
    }
  },
  methods: {
    extractDetail(detail) {
      if (!detail) {
        const common_answers = this.commonQuestions.map(question => {
          const options = question.options.map(o => ({ ...o }))
          const answers = question.type === 0 ? null : []

          return {
            id: null,
            type: question.type,
            sequence: question.sequence,
            is_modified: false,
            question_id: question.id,
            title: question.title,
            note: question.node,
            options,
            answers,
            answer: null,
            cost: 0
          }
        })

        const spot_answers = this.spotQuestions.map(question => {
          return {
            id: null,
            is_modified: false,
            question_id: question.id,
            title: question.title,
            note: question.note,
            sequence: question.sequence,
            spot_options: question.spot_options,
            selected_spot_id: null,
            selected_spot: null,
            selected_spot_name: null,
            cost: 0
          }
        })

        return {
          id: null,
          date_of_birth: null,
          passport_expire: null,
          common_answers,
          spot_answers
        }
      }

      const date_of_birth =
        detail.date_of_birth && new Date(Date.parse(detail.date_of_birth))
      const passport_expire =
        detail.passport_expire && new Date(Date.parse(detail.passport_expire))

      const common_answers = this.commonQuestions.map(question => {
        const common_answer = detail.common_answers.find(
          answer => answer.question_id === question.id
        )

        const options = common_answer.options.map(o => ({ ...o }))

        let answers

        if (common_answer.answer) {
          if (question.type === 0) {
            answers = options.find(op =>
              op.type === 'Text'
                ? op.value === common_answer.answer
                : op.name === common_answer.answer
            )
          } else {
            answers = common_answer.answer
              .split(',')
              .map(a =>
                options.find(op =>
                  op.type === 'Text' ? op.value === a : op.name === a
                )
              )
          }
        } else {
          if (question.type === 0) {
            answers = null
          } else {
            answers = []
          }
        }

        return {
          ...common_answer,
          options,
          answers
        }
      })

      const spot_answers = this.spotQuestions.map(question => {
        const answer = detail.spot_answers.find(
          a => a.question_id === question.id
        )

        return {
          ...answer,
          spot_options: question.spot_options
        }
      })

      return {
        ...detail,
        date_of_birth,
        passport_expire,
        common_answers,
        spot_answers
      }
    },
    onEditing() {
      if (!this.canEdit) {
        return
      }

      this.editing = true
    },
    onSelectedSpot(question, spot_option) {
      question.selected_spot = spot_option
      question.selected_spot_id = spot_option.id
      question.selected_spot_name = spot_option.name
    },
    async onSave() {
      const errors = this.checkCustomerInfo()
      if (errors.length > 0) {
        return notifyService.error(errors[0])
      }

      const answers_errors = this.checkCommonAnswers()
      if (answers_errors.length > 0) {
        return notifyService.error(answers_errors[0])
      }

      const common_answers = this.genCommonAnswers()
      const spot_answers = this.detail.spot_answers.map(answer => ({
        ...answer,
        spot_options: answer.spot_options.map(o => o.id).join(',')
      }))

      const date_of_birth = this.formatDate(this.detail.date_of_birth)
      const passport_expire = this.formatDate(this.detail.passport_expire)

      // Sending detail
      const api = `${USER_ORDER_DETAILS_API}/${this.detail.id}`
      const data = {
        ...this.detail,
        date_of_birth,
        passport_expire,
        common_answers,
        spot_answers
      }

      try {
        this.isSending = true
        const res = await this.axios.put(api, data)
        const result = res.data

        if (result.success) {
          notifyService.success('資料更新成功')
          this.editing = false
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('save detail error: ', e)
        notifyService.update_error(e)
      } finally {
        this.isSending = false
      }
    },
    onCancel() {
      this.detail = this.extractDetail(this.originDetail)
      this.editing = false
    },
    checkCustomerInfo() {
      let errors = []

      const requiredAttributes = [
        'name',
        'emergency_contact',
        'emergency_phone',
        'date_of_birth',
        'email',
        'mobile_phone'
      ]

      for (let attribute of requiredAttributes) {
        if (!this.detail[attribute]) {
          errors.push(this.requiredError(attribute))
        }
      }

      return errors
    },
    checkCommonAnswers() {
      return this.detail.common_answers.reduce((errors, question) => {
        if (question.type === 0) {
          if (!question.answers) {
            errors.push(`參團資料 題目「${question.title}」必選`)
          } else {
            if (question.answers.type === 'Text' && !question.answers.value) {
              errors.push(
                `參團資料 題目「${question.title}」選項「${question.answers.name}」 不可空白`
              )
            }
          }
        } else {
          if (question.answers.length === 0) {
            errors.push(`參團資料 題目「${question.title}」至少選ㄧ`)
          } else {
            question.answers.forEach(option => {
              if (option.type === 'Text' && !option.value) {
                errors.push(
                  `參團資料 題目「${question.title}」選項「${option.name}」 不可空白`
                )
              }
            })
          }
        }
        return errors
      }, [])
    },
    genCommonAnswers() {
      return this.detail.common_answers.map(question => {
        let options, answers, answer

        if (question.type === 0) {
          options = question.options.map(option => {
            let value
            if (option.type === 'Option') {
              value = option.name === question.answers.name ? 'true' : 'false'
            } else {
              value = option.value
            }

            return {
              ...option,
              value
            }
          })
          answers = options.find(
            option => option.name === question.answers.name
          )
          answer = answers.type === 'Option' ? answers.name : answers.value
        } else {
          options = question.options.map(option => {
            let value
            if (option.type === 'Option') {
              const index = question.answers.findIndex(
                a => a.name === option.name
              )
              value = index !== -1 ? 'true' : 'false'
            } else {
              value = option.value
            }

            return {
              ...option,
              value
            }
          })
          answers = question.answers.map(answer =>
            options.find(option => option.name === answer.name)
          )
          answer = answers
            .map(option => {
              return option.type === 'Option' ? option.name : option.value
            })
            .join(',')
        }

        return {
          ...question,
          options,
          answers,
          answer
        }
      })
    },
    updateAge(birthDate, dep, childAge, babyAge) {
      let ageType = 0
      const depDate = new Date(Date.parse(dep))

      if (!birthDate || !depDate) {
        return ageType
      }

      let age = depDate.getFullYear() - birthDate.getFullYear()
      const date = new Date(
        depDate.getFullYear() - age,
        depDate.getMonth(),
        depDate.getDate()
      )
      // Go back to the year the person was born in case of a leap year
      if (birthDate > date) {
        age--
      }

      if (age >= childAge) {
        ageType = 0 // adult
      } else if (age >= babyAge) {
        ageType = 1 // child
      } else {
        ageType = 2 // Baby
      }

      this.detail.age = age
      this.detail.age_type = ageType
    },
    requiredError(attribute) {
      return this.$t('validation.required', {
        attribute: this.$t(`attributes.${attribute}`)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.mark-remove-detail {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  color: red;
  background-color: #c3c3c3a1;
  font-size: 3em;
  font-weight: bold;
  line-height: 2;
  z-index: 30;
}
</style>
